import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';
import { enqueueCartMutation, cartNeedsRestoration } from '@theme/cart-synchronization';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorService - The unconfirmed update error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'cartDiscountErrorService'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #setBusy(busy) {
    if (busy) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');
    for (const control of this.querySelectorAll('button, input')) control.disabled = busy;
  }

  #clearErrors() {
    for (const ref of ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping', 'cartDiscountErrorService']) {
      this.refs[ref].classList.add('hidden');
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#activeFetch || cartNeedsRestoration()) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;
    const normalizedCode = discountCodeValue.toLowerCase();

    if (this.#existingDiscounts().some((code) => code.toLowerCase() === normalizedCode)) return;

    const abortController = this.#createAbortController();
    this.#clearErrors();
    this.#setBusy(true);

    return enqueueCartMutation(async () => {
      if (!this.isConnected) {
        this.#activeFetch = null;
        this.#setBusy(false);
        return;
      }
      this.#setBusy(true);
      const existingDiscounts = this.#existingDiscounts();
      const allDiscountCodes = [...existingDiscounts, discountCodeValue];
      const deferredPromise = CartDiscountUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: allDiscountCodes.map((code) => ({ code })),
          promise: deferredPromise.promise,
        })
      );

      try {
        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: allDiscountCodes.join(','),
            sections: [this.dataset.sectionId],
          }),
        });

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });

        const data = await response.json();
        if (!response.ok || data.errors || !Array.isArray(data.discount_codes)) throw new Error('Discount update could not be confirmed');

        const newHtml = data.sections?.[this.dataset.sectionId]
          || await sectionRenderer.getSectionHTML(this.dataset.sectionId, false);
        const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
        const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
        if (!section) throw new Error('Cart details could not be refreshed');

        if (
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code.toLowerCase() === normalizedCode && discount.applicable === false;
          })
        ) {
          discountCode.value = '';
          // Other discounts can expire or become ineligible even when the new code is rejected.
          await morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
          this.#handleDiscountError('discount_code');
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          return;
        }

        const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
        if (section) {
          const codes = Array.from(discountCodes)
            .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
            .filter(Boolean);
          // Before morphing, we need to check if the shipping discount is applicable in the UI
          // we check the liquid logic compared to the cart payload to assess whether we leveraged
          // a valid shipping discount code.
          if (
            codes.length === existingDiscounts.length &&
            codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
            data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return discount.code.toLowerCase() === normalizedCode && discount.applicable === true;
            })
          ) {
            discountCode.value = '';
            await morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
            this.#handleDiscountError('shipping');
            deferredPromise.resolve({
              cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
            });
            return;
          }
        }

        // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
        // morphSection no longer syncs the input value from the server-rendered empty state,
        // so without this the user's typed code stays in the field after a successful apply.
        discountCode.value = '';
        await morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
      } catch (error) {
        deferredPromise.reject(error);
        if (error instanceof Error && error.name !== 'AbortError') {
          this.#handleDiscountError('service');
          this.dispatchEvent(
            new CartErrorEvent({
              error: error.message || 'Failed to apply discount',
              code: 'SERVICE_UNAVAILABLE',
            })
          );
        }
      } finally {
        this.#activeFetch = null;
        this.#setBusy(false);
        cartPerformance.measureFromEvent('discount-update:user-action', event);
      }
    });
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#activeFetch || cartNeedsRestoration()) return;

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    const abortController = this.#createAbortController();
    this.#clearErrors();
    this.#setBusy(true);
    return enqueueCartMutation(async () => {
      if (!this.isConnected) {
        this.#activeFetch = null;
        this.#setBusy(false);
        return;
      }
      this.#setBusy(true);
      const remainingDiscounts = this.#existingDiscounts().filter(code => code !== discountCode);
      const deferredPromise = CartDiscountUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: remainingDiscounts.map((code) => ({ code })),
          promise: deferredPromise.promise,
        })
      );

      try {
        const config = fetchConfig('json', {
          body: JSON.stringify({ discount: remainingDiscounts.join(','), sections: [this.dataset.sectionId] }),
        });

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });

        const data = await response.json();
        if (!response.ok || data.errors || !Array.isArray(data.discount_codes)) throw new Error('Discount update could not be confirmed');

        const newHtml = data.sections?.[this.dataset.sectionId]
          || await sectionRenderer.getSectionHTML(this.dataset.sectionId, false);
        await morphSection(this.dataset.sectionId, newHtml, {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });

        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
      } catch (error) {
        deferredPromise.reject(error);
        if (error instanceof Error && error.name !== 'AbortError') {
          this.#handleDiscountError('service');
          this.dispatchEvent(
            new CartErrorEvent({
              error: error.message || 'Failed to remove discount',
              code: 'SERVICE_UNAVAILABLE',
            })
          );
        }
      } finally {
        this.#activeFetch = null;
        this.#setBusy(false);
      }
    });
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'service'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountErrorService } = this.refs;
    const target = type === 'service' ? cartDiscountErrorService : type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
    if (type === 'service') return; // The request handler dispatches the service error event.

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
